/**
 * Fixed copy and limits for operational issues (PRO-3100 TIP §4 validation, TIP-D14, TIP-D18).
 * Every length here is the AC catalogue value; the 0227 CHECKs carry the same numbers.
 */
import type {
  OpsEscalationLevel,
  OpsNotificationEvent,
  OpsRequestType,
  OpsThresholdKey,
} from '../types/opsIssues';

/** TIP-D18: shown in the export header. No disposal job in Increment 1. */
export const OPS_ISSUE_RETENTION_YEARS = 6;

export const OPS_LIMITS = {
  title: 120,
  description: 2000,
  update: 2000,
  summary: 1000,
  returnReason: 1000,
  reopenReason: 1000,
  confirmNote: 500,
  actionDescription: 500,
  completionNote: 500,
  redateReason: 500,
  cancelReason: 500,
  statement: 1000,
  response: 2000,
  categoryLabel: 80,
  severityDefinition: 200,
  respondActions: 5,
} as const;

/** Inclusive whole-number range per threshold (E27 refuses anything outside it). */
export const OPS_THRESHOLD_RANGE: Readonly<Record<OpsThresholdKey, readonly [number, number]>> = {
  long_running_weeks: [2, 12],
  confirmation_overdue_working_days: [2, 10],
};

export const OPS_PAGE_SIZE = 50;
/** Per-school row cap for list and portfolio reads (TIP §6 `OpsPortfolioService`). */
export const OPS_SCHOOL_ROW_CAP = 500;
/** E2 returns at most this many owners. */
export const OPS_OWNER_PICKER_MAX = 200;

/** Shown under every free-text field (ISS-1.1). */
export const OPS_FREE_TEXT_RULE =
  'Do not include child names or identifying detail. Safeguarding concerns go to the DSL process; staff conduct goes to HR cases.';

export const OPS_NAME_NOT_RECORDED = 'Name not recorded';

export const OPS_LEVEL_LABEL: Readonly<Record<OpsEscalationLevel, string>> = {
  operations_director: 'Operations Director',
  coo: 'COO',
  executive: 'Executive',
};

export const OPS_REQUEST_TYPE_LABEL: Readonly<Record<OpsRequestType, string>> = {
  awareness: 'Awareness',
  support: 'Support',
  decision: 'Decision',
  resource: 'Resource',
  intervention: 'Intervention',
};

/** One sentence per email event (TIP-D14). Coded and fixed: no free text can reach it. */
export const OPS_EMAIL_SENTENCE: Readonly<Record<OpsNotificationEvent, string>> = {
  issue_owner_assigned: 'You are now the owner of an operational issue.',
  action_assigned: 'You have been given an action on an operational issue.',
  resolution_proposed: 'A resolution has been proposed and needs your confirmation.',
  resolution_confirmed: 'A proposed resolution has been confirmed.',
  resolution_returned: 'A proposed resolution has been returned.',
  escalation_raised: 'An operational issue has been escalated to you.',
  escalation_acknowledged: 'Your escalation has been acknowledged.',
  escalation_responded: 'Your escalation has a response.',
};

export function opsEmailSubject(event: OpsNotificationEvent, categoryLabel: string): string {
  const head: Record<OpsNotificationEvent, string> = {
    issue_owner_assigned: 'Issue assigned',
    action_assigned: 'Action assigned',
    resolution_proposed: 'Resolution to confirm',
    resolution_confirmed: 'Resolution confirmed',
    resolution_returned: 'Resolution returned',
    escalation_raised: 'Escalation raised',
    escalation_acknowledged: 'Escalation acknowledged',
    escalation_responded: 'Escalation responded',
  };
  return `Weekly Report: ${head[event]} (${categoryLabel})`;
}
